import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Building2, Users, FileText, Image, Calendar, Newspaper } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const ICONS = {
    society: Building2,
    committee: Users,
    document: FileText,
    gallery: Image,
    event: Calendar,
    news: Newspaper,
};

export const GlobalSearch = ({ open, onClose }) => {
    const [q, setQ] = useState("");
    const [hits, setHits] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) { setQ(""); setHits([]); return; }
    }, [open]);

    useEffect(() => {
        if (!open || q.trim().length < 2) { setHits([]); return; }
        let cancel = false;
        setLoading(true);
        const t = setTimeout(() => {
            api.get("/search", { params: { q, limit: 20 } })
                .then((r) => { if (!cancel) setHits(r.data || []); })
                .finally(() => { if (!cancel) setLoading(false); });
        }, 200);
        return () => { cancel = true; clearTimeout(t); };
    }, [q, open]);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl p-0" data-testid="global-search-dialog">
                <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
                    <Search size={18} className="text-slate-400" />
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search societies, notices, events, news…"
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                        data-testid="global-search-input"
                    />
                    <button onClick={onClose} className="rounded-full p-1 text-slate-500 hover:bg-slate-100">
                        <X size={16} />
                    </button>
                </div>
                <div className="max-h-96 overflow-y-auto p-2">
                    {loading && <div className="p-5 text-sm text-slate-500">Searching…</div>}
                    {!loading && q.trim().length >= 2 && hits.length === 0 && (
                        <div className="p-5 text-sm text-slate-500">No results for "{q}"</div>
                    )}
                    {!loading && hits.map((h) => {
                        const Icon = ICONS[h.kind] || FileText;
                        return (
                            <Link
                                key={`${h.kind}-${h.id}`}
                                to={h.url || "/"}
                                onClick={onClose}
                                className="flex items-start gap-3 rounded-xl px-3 py-3 hover:bg-slate-50"
                                data-testid={`search-hit-${h.kind}`}
                            >
                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-blue-light text-brand-blue">
                                    <Icon size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium text-slate-800">{h.title}</div>
                                    <div className="truncate text-xs text-slate-500">{h.snippet || h.kind}</div>
                                </div>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">{h.kind}</span>
                            </Link>
                        );
                    })}
                    {q.trim().length < 2 && (
                        <div className="p-6 text-center text-xs text-slate-400">Type at least 2 characters</div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
